import type { GameState, OwnedStone, Rarity, Stone } from './types';
import { getStoneById } from '../data/stones';

/** 連続ログイン日数に応じた本日の成長ポイント。
 *  1日目=100, 2日目=110, ..., 30日目=390, それ以降も390で固定。 */
export function calculateTodayGrowthPoints(consecutiveDays: number): number {
  if (consecutiveDays <= 1) return 100;
  const day = Math.min(consecutiveDays, 30);
  return 100 + (day - 1) * 10;
}

/** レアリティに対応する最大段階数(段階0=原石、最大段階=覚醒) */
export function maxStageForRarity(rarity: Rarity): number {
  switch (rarity) {
    case 'low':
      return 3;
    case 'mid':
      return 4;
    case 'high':
      return 5;
  }
}

/** 段階n(0始まり)に到達するために累計で必要な成長ポイントの閾値。
 *  全レアリティ一律: 段階1=100pt, 段階2=200pt, 段階3=300pt ...
 *  例えば low の 段階2 への閾値は 200pt(0→100→200 と段階UPする)。*/
export function thresholdForStage(_rarity: Rarity, stage: number): number {
  if (stage <= 0) return 0;
  return stage * 100;
}

/** 所持石の累計ポイントから現在の段階を逆算 */
export function computeCurrentStage(rarity: Rarity, growthPoints: number): number {
  const max = maxStageForRarity(rarity);
  let stage = 0;
  for (let s = 1; s <= max; s++) {
    if (growthPoints >= thresholdForStage(rarity, s)) {
      stage = s;
    } else {
      break;
    }
  }
  return stage;
}

/** 次の段階までに必要な残ポイント */
export function pointsToNextStage(stone: Stone, owned: OwnedStone): number | null {
  const max = maxStageForRarity(stone.rarity);
  if (owned.currentStage >= max) return null;
  const nextThreshold = thresholdForStage(stone.rarity, owned.currentStage + 1);
  return Math.max(0, nextThreshold - owned.growthPoints);
}

/** 選択中の石をタップして育てる。
 *  戻り値の {state, stageUp} でステート更新と段階UP通知を呼び分け可能。 */
export interface TapResult {
  state: GameState;
  gainedPoints: number;
  energyGained: number;
  stageUp: boolean;
  newStage: number;
}

export function applyTap(state: GameState): TapResult {
  if (state.daily.tapUsed) {
    return {
      state,
      gainedPoints: 0,
      energyGained: 0,
      stageUp: false,
      newStage: state.inventory.find((s) => s.stoneId === state.selectedStoneId)?.currentStage ?? 0,
    };
  }
  if (!state.selectedStoneId) {
    return {
      state,
      gainedPoints: 0,
      energyGained: 0,
      stageUp: false,
      newStage: 0,
    };
  }

  const stone = getStoneById(state.selectedStoneId);
  if (!stone) {
    return { state, gainedPoints: 0, energyGained: 0, stageUp: false, newStage: 0 };
  }

  const gainedPoints = calculateTodayGrowthPoints(state.daily.consecutiveDays);
  const energyGained = 1;
  let stageUp = false;
  let newStage = 0;

  const nextInventory = state.inventory.map((owned) => {
    if (owned.stoneId !== state.selectedStoneId) return owned;
    const newPoints = owned.growthPoints + gainedPoints;
    const calculatedStage = computeCurrentStage(stone.rarity, newPoints);
    if (calculatedStage > owned.currentStage) {
      stageUp = true;
      newStage = calculatedStage;
    } else {
      newStage = calculatedStage;
    }
    return {
      ...owned,
      growthPoints: newPoints,
      currentStage: calculatedStage,
      tapCount: owned.tapCount + 1,
    };
  });

  return {
    state: {
      ...state,
      inventory: nextInventory,
      daily: {
        ...state.daily,
        tapUsed: true,
        energy: state.daily.energy + energyGained,
      },
    },
    gainedPoints,
    energyGained,
    stageUp,
    newStage,
  };
}

/** 新規石入手(ガチャ/ショップ共通) */
export function acquireStone(state: GameState, stoneId: string): GameState {
  const exists = state.inventory.find((s) => s.stoneId === stoneId);
  if (exists) {
    // 既所持なら何もせず、呼び出し側で売却等の処理を行う想定
    return state;
  }
  const stone = getStoneById(stoneId);
  if (!stone) return state;

  return {
    ...state,
    inventory: [
      ...state.inventory,
      {
        stoneId,
        acquiredAt: Date.now(),
        growthPoints: 0,
        currentStage: 0,
        tapCount: 0,
      },
    ],
  };
}

/** 重複石を売却してエネルギーを得る(中/レアはエネルギー多め) */
export function sellDuplicate(stoneId: string): number {
  const stone = getStoneById(stoneId);
  if (!stone) return 0;
  switch (stone.rarity) {
    case 'low':
      return 3;
    case 'mid':
      return 10;
    case 'high':
      return 30;
  }
}
